import { useState } from 'react';
import type { ReactElement } from 'react';
import {
  BACKUPANLASS_BESCHRIFTUNG,
  BACKUPART_BESCHRIFTUNG,
  MAX_SICHERUNGSSTAENDE,
  MIN_BACKUP_PASSWORT_LAENGE,
} from '@mcikm-vote/shared';
import type { BackupEintrag } from '@mcikm-vote/shared';

import {
  backupDateiPfad,
  useBackupErzeugen,
  useBackupLoeschen,
  useBackups,
} from '../../admin/backupsApi';
import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { Feld } from '../../components/Feld';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { formatiereGroesse } from '../../components/ExportFormatZeile';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';
import { formatiereZeitpunkt } from '../../formatierung';
import { WiederherstellungAbschnitt } from './WiederherstellungAbschnitt';

/**
 * Backups und Wiederherstellung (§20.1, §28).
 *
 * Die Seite vereint die drei Teile aus §28: manuelle Backups erzeugen und verwalten (§28.1), die
 * automatischen Sicherungsstände einsehen (§28.3) und den Datenbestand wiederherstellen (§28.2).
 *
 * **Der Hinweis auf das Passwort steht bewusst vor dem Formular.** Ein verlorenes Backup-Passwort
 * macht die Datei wertlos; das ist nach dem Erzeugen nicht mehr zu heilen und darf niemanden
 * überraschen.
 */
export function BackupsSeite(): ReactElement {
  const backups = useBackups();
  const erzeugen = useBackupErzeugen();
  const loeschen = useBackupLoeschen();

  const [passwort, setPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [zuLoeschen, setZuLoeschen] = useState<BackupEintrag | null>(null);

  const zuKurz = passwort.length > 0 && passwort.length < MIN_BACKUP_PASSWORT_LAENGE;
  const ungleich = wiederholung.length > 0 && passwort !== wiederholung;
  const bereit = passwort.length >= MIN_BACKUP_PASSWORT_LAENGE && passwort === wiederholung;

  const fehler = erzeugen.error ?? loeschen.error ?? null;
  const laeuft = erzeugen.isPending || loeschen.isPending;

  const eintraege = backups.data ?? [];
  const sicherungsstaende = eintraege.filter((eintrag) => eintrag.art === 'sicherungsstand').length;

  function erzeugeBackup(): void {
    erzeugen.mutate(passwort, {
      onSuccess: () => {
        setPasswort('');
        setWiederholung('');
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Backups und Wiederherstellung</h1>

      {fehler !== null && <Fehlermeldung>{fehler.message}</Fehlermeldung>}

      {zuLoeschen !== null && (
        <BestaetigungsDialog
          titel="Backupdatei löschen?"
          beschreibung={
            <>
              Die Datei <code>{zuLoeschen.dateiname}</code> wird gelöscht. Anders als bei einem
              Export lässt sie sich <strong>nicht</strong> neu erzeugen — sie enthält einen Stand,
              den es sonst nirgends mehr gibt.
            </>
          }
          bestaetigungstext="Endgültig löschen"
          gefahr
          laeuft={loeschen.isPending}
          onBestaetigen={() => {
            loeschen.mutate(zuLoeschen.dateiname, { onSuccess: () => setZuLoeschen(null) });
          }}
          onAbbrechen={() => {
            setZuLoeschen(null);
          }}
        />
      )}

      <Karte>
        <h2 className="text-base font-semibold">Neues Backup erzeugen</h2>
        <p className="text-text-gedaempft mt-1 text-sm">
          Das Backup enthält Veranstaltungen, Abstimmungen, Tokens, Anwesenheit und alle Ergebnisse
          — nicht aber den Administratorzugang, die Schlüssel aus der <code>.env</code> oder aktive
          Sitzungen.
        </p>

        <p className="border-warnung/50 bg-warnung/10 mt-3 rounded-md border p-3 text-sm">
          <strong>Das Passwort wird nicht gespeichert.</strong> Es ist der einzige Schutz der Datei
          und zugleich der einzige Weg zurück: Ohne das Passwort lässt sich das Backup nicht mehr
          einspielen. Bitte im Passwortmanager ablegen — getrennt vom{' '}
          <code>TOKEN_ENCRYPTION_KEY</code>.
        </p>

        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(ereignis) => {
            ereignis.preventDefault();
            if (bereit) {
              erzeugeBackup();
            }
          }}
        >
          <Feld
            name="backup-passwort"
            beschriftung="Backup-Passwort"
            typ="password"
            wert={passwort}
            pflicht
            hinweis={`Mindestens ${MIN_BACKUP_PASSWORT_LAENGE} Zeichen.`}
            onAendern={setPasswort}
          />
          {zuKurz && (
            <Fehlermeldung>
              Das Passwort muss mindestens {MIN_BACKUP_PASSWORT_LAENGE} Zeichen lang sein.
            </Fehlermeldung>
          )}

          <Feld
            name="backup-passwort-wiederholung"
            beschriftung="Passwort wiederholen"
            typ="password"
            wert={wiederholung}
            pflicht
            onAendern={setWiederholung}
          />
          {ungleich && <Fehlermeldung>Die beiden Eingaben stimmen nicht überein.</Fehlermeldung>}

          <div>
            <Knopf type="submit" disabled={!bereit || laeuft}>
              {erzeugen.isPending ? 'Backup wird erzeugt …' : 'Backup erzeugen'}
            </Knopf>
          </div>
        </form>
      </Karte>

      <Karte>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">Vorhandene Backups</h2>
          <span className="text-text-gedaempft text-sm">
            {sicherungsstaende} von höchstens {MAX_SICHERUNGSSTAENDE} automatischen
            Sicherungsständen
          </span>
        </div>

        {backups.isPending ? (
          <p className="text-text-gedaempft mt-2 text-sm">Wird geladen …</p>
        ) : backups.isError ? (
          <div className="mt-2">
            <Fehlermeldung>{backups.error.message}</Fehlermeldung>
          </div>
        ) : eintraege.length === 0 ? (
          <p className="text-text-gedaempft mt-2 text-sm">
            Es liegt noch kein Backup vor. Vor jeder Veranstaltung und vor jeder Aktualisierung
            sollte eines erzeugt werden.
          </p>
        ) : (
          <ul className="mt-3 flex flex-col gap-3">
            {eintraege.map((eintrag) => (
              <li
                key={eintrag.dateiname}
                className="border-rand flex flex-wrap items-center gap-3 rounded-lg border p-3"
              >
                <div className="grow">
                  <p className="font-medium">{BACKUPART_BESCHRIFTUNG[eintrag.art]}</p>
                  <p className="text-text-gedaempft text-sm">
                    {formatiereZeitpunkt(eintrag.erstelltAm)} ·{' '}
                    {formatiereGroesse(eintrag.groesseBytes)} ·{' '}
                    {BACKUPANLASS_BESCHRIFTUNG[eintrag.anlass]}
                  </p>
                  <p className="text-text-gedaempft mt-1 font-mono text-xs">{eintrag.dateiname}</p>

                  {/* §28.2: Beides entscheidet darüber, ob sich die Datei überhaupt einspielen
                      lässt — es gehört sichtbar an die Datei und nicht erst in den Fehlerfall. */}
                  {!eintrag.passtZumSchema && (
                    <p className="text-warnung mt-1 text-sm">
                      Aus Version {eintrag.anwendungsversion} mit abweichendem Datenbankstand — für
                      eine Wiederherstellung ist zuerst diese Version zu starten.
                    </p>
                  )}
                  {eintrag.passtZumSchema && !eintrag.gleicherTokenschluessel && (
                    <p className="text-warnung mt-1 text-sm">
                      Mit einem anderen <code>TOKEN_ENCRYPTION_KEY</code> erzeugt — eine
                      Wiederherstellung verlangt zusätzlich den alten Schlüssel.
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <a
                    href={backupDateiPfad(eintrag.dateiname)}
                    className="border-rand bg-flaeche-gedaempft hover:bg-rand min-h-touchflaeche inline-flex items-center rounded-md border px-4 font-medium transition"
                  >
                    Herunterladen
                  </a>
                  <Knopf
                    art="sekundaer"
                    disabled={laeuft}
                    onClick={() => {
                      setZuLoeschen(eintrag);
                    }}
                  >
                    Löschen
                  </Knopf>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Karte>

      <WiederherstellungAbschnitt
        backups={eintraege}
        onFertig={() => {
          void backups.refetch();
        }}
      />
    </div>
  );
}
